import React from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { usePermissions, RESOURCES, ACTIONS } from '@/contexts/PermissionsContext';
import { EmailModal, WhatsAppModal, FollowUpModal, ProformaModal, CatalogModal } from '@/components/Leads';
import { STAGES, LEAD_SOURCES, BACKEND_URL, getAuthHeader, formatDateDDMMYYYY, getAvatarColor } from '@/components/Leads/leadsConstants';
import { useUploadGuard } from '@/components/UploadGuard';

function LeadsList({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canDelete, canUpdate, canCreate, roleName, isViewer } = usePermissions();
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [salesReps, setSalesReps] = React.useState([]);
  const { ensureCompanyId, guardModal } = useUploadGuard(user);
  const [selectedLeads, setSelectedLeads] = React.useState([]);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = React.useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = React.useState(false);
  const [showEmailModal, setShowEmailModal] = React.useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = React.useState(false);
  const [showProformaModal, setShowProformaModal] = React.useState(false);
  const [showCatalogModal, setShowCatalogModal] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState(null);
  const [bulkUpdateData, setBulkUpdateData] = React.useState({});
  
  // New leads import & Google Sheets sync state
  const [showNewImportModal, setShowNewImportModal] = React.useState(false);
  const [showGoogleSheetsModal, setShowGoogleSheetsModal] = React.useState(false);
  const [importResult, setImportResult] = React.useState(null);
  const [isImporting, setIsImporting] = React.useState(false);
  const [googleSheetUrl, setGoogleSheetUrl] = React.useState('');
  const [googleSheetsStatus, setGoogleSheetsStatus] = React.useState({ connected: false });
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [showImportDropdown, setShowImportDropdown] = React.useState(false);
  
  // View mode toggle: 'all' or 'followup'
  const [viewMode, setViewMode] = React.useState('all');
  const [allFetchedLeads, setAllFetchedLeads] = React.useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  
  // Get today's date for default filter
  const getToday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Default stages (exclude Won and Lost)
  const getDefaultStages = () => {
    return STAGES.filter(s => s.value !== 'won' && s.value !== 'lost').map(s => s.value);
  };

  // All priorities
  const ALL_PRIORITIES = ['hot', 'warm', 'cold'];
  
  // Sort order: 'asc' = oldest first (default for follow-up), 'desc' = latest first
  const [sortOrder, setSortOrder] = React.useState('asc');
  
  // Date helper: get date string offset from today
  const getDateOffset = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  
  // Lead sources list - dynamically loaded from masters
  const [leadSourcesList, setLeadSourcesList] = React.useState([]);
  
  // Filters - all filters default to "all selected"
  const [filters, setFilters] = React.useState({
    stages: searchParams.get('stage') ? [searchParams.get('stage')] : getDefaultStages(),
    priorities: [...ALL_PRIORITIES],  // All priorities selected by default
    assigned_to: [],  // Will be populated when salesReps load
    sources: [],  // Will be populated when leadSources load
    sub_sources: [],  // Will be populated when subSources load
    search: '',
    followup_date_from: '',  // Empty = no filter
    followup_date_to: '',    // Empty = no filter
    created_date_from: getToday(),
    created_date_to: getToday()
  });
  
  // Master data for sub-sources
  const [subSourcesList, setSubSourcesList] = React.useState([]);
  
  // Multi-filter dropdowns visibility
  const [showStageFilter, setShowStageFilter] = React.useState(false);
  const [showPriorityFilter, setShowPriorityFilter] = React.useState(false);
  const [showSalesRepFilter, setShowSalesRepFilter] = React.useState(false);
  const [showSourceFilter, setShowSourceFilter] = React.useState(false);
  const [showSubSourceFilter, setShowSubSourceFilter] = React.useState(false);

  // Handle stage filter toggle
  const toggleStageFilter = (stageValue) => {
    setFilters(prev => {
      const currentStages = prev.stages || [];
      if (currentStages.includes(stageValue)) {
        return { ...prev, stages: currentStages.filter(s => s !== stageValue) };
      } else {
        return { ...prev, stages: [...currentStages, stageValue] };
      }
    });
  };

  // Handle priority filter toggle
  const togglePriorityFilter = (priorityValue) => {
    setFilters(prev => {
      const currentPriorities = prev.priorities || [];
      if (currentPriorities.includes(priorityValue)) {
        return { ...prev, priorities: currentPriorities.filter(p => p !== priorityValue) };
      } else {
        return { ...prev, priorities: [...currentPriorities, priorityValue] };
      }
    });
  };

  // Handle sales rep filter toggle
  const toggleSalesRepFilter = (repName) => {
    setFilters(prev => {
      const currentReps = prev.assigned_to || [];
      if (currentReps.includes(repName)) {
        return { ...prev, assigned_to: currentReps.filter(r => r !== repName) };
      } else {
        return { ...prev, assigned_to: [...currentReps, repName] };
      }
    });
  };

  // Handle source filter toggle
  const toggleSourceFilter = (sourceName) => {
    setFilters(prev => {
      const currentSources = prev.sources || [];
      if (currentSources.includes(sourceName)) {
        return { ...prev, sources: currentSources.filter(s => s !== sourceName) };
      } else {
        return { ...prev, sources: [...currentSources, sourceName] };
      }
    });
  };

  // Clear date filters
  const clearDateFilters = () => {
    setFilters(prev => ({
      ...prev,
      followup_date_from: '',
      followup_date_to: ''
    }));
  };

  // Clear created date filters
  const clearCreatedDateFilters = () => {
    setFilters(prev => ({
      ...prev,
      created_date_from: '',
      created_date_to: ''
    }));
  };

  // Track if sales reps have been loaded
  const [salesRepsLoaded, setSalesRepsLoaded] = React.useState(false);
  const [initialLoadDone, setInitialLoadDone] = React.useState(false);

  // Fetch sales reps and Google Sheets status on mount
  React.useEffect(() => {
    fetchSalesReps();
    fetchSubSources();
    fetchLeadSources();
    fetchGoogleSheetsStatus();
  }, []);

  // Once sales reps are loaded, do the initial fetch
  React.useEffect(() => {
    if (salesRepsLoaded && !initialLoadDone) {
      setInitialLoadDone(true);
      fetchLeads();
    }
  }, [salesRepsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch leads when filters change (after initial load)
  React.useEffect(() => {
    if (initialLoadDone) {
      setCurrentPage(1);
      fetchLeads();
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply viewMode filter client-side (no re-fetch needed)
  React.useEffect(() => {
    if (viewMode === 'followup') {
      const today = getToday();
      // Pre-fill date inputs with today if empty
      if (!filters.followup_date_from && !filters.followup_date_to) {
        setFilters(prev => ({ ...prev, followup_date_from: today, followup_date_to: today }));
        return; // Will re-run with filled dates
      }
      let followUpLeads = allFetchedLeads.filter(lead => lead.next_follow_up_date);
      // Filter by follow-up date range
      const fromDate = filters.followup_date_from || today;
      const toDate = filters.followup_date_to || today;
      followUpLeads = followUpLeads.filter(lead => lead.next_follow_up_date >= fromDate && lead.next_follow_up_date <= toDate);
      // Sort by follow-up date + time
      followUpLeads.sort((a, b) => {
        const dateA = (a.next_follow_up_date || '') + ' ' + (a.next_follow_up_time || '00:00');
        const dateB = (b.next_follow_up_date || '') + ' ' + (b.next_follow_up_time || '00:00');
        return sortOrder === 'desc' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
      });
      setLeads(followUpLeads);
    } else {
      // All Leads: sort by created_at date + time
      const sorted = [...allFetchedLeads].sort((a, b) => {
        const dateA = a.created_at || '';
        const dateB = b.created_at || '';
        return sortOrder === 'desc' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
      });
      setLeads(sorted);
    }
    setCurrentPage(1);
  }, [viewMode, allFetchedLeads, filters.followup_date_from, filters.followup_date_to, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Handle multi-select stages
      if (filters.stages && filters.stages.length > 0 && filters.stages.length < STAGES.length) {
        filters.stages.forEach(stage => params.append('stage', stage));
      }
      // Don't send priority/assigned_to/source params to backend - we filter client-side
      if (filters.search) params.append('search', filters.search);
      if (filters.followup_date_from) params.append('followup_date_from', filters.followup_date_from);
      if (filters.followup_date_to) params.append('followup_date_to', filters.followup_date_to);
      if (filters.created_date_from) params.append('date_from', filters.created_date_from);
      if (filters.created_date_to) params.append('date_to', filters.created_date_to);
      
      const response = await axios.get(`${BACKEND_URL}/api/leads?${params.toString()}`, getAuthHeader());
      
      // Client-side filtering for multi-select
      let filteredLeads = response.data;
      
      // Filter by stages if NOT all selected
      if (filters.stages && filters.stages.length > 0 && filters.stages.length < STAGES.length) {
        filteredLeads = filteredLeads.filter(lead => filters.stages.includes(lead.stage));
      }
      
      // Filter by priorities if NOT all selected (all 3 = no filter)
      if (filters.priorities && filters.priorities.length > 0 && filters.priorities.length < ALL_PRIORITIES.length) {
        filteredLeads = filteredLeads.filter(lead => filters.priorities.includes(lead.priority));
      }
      
      // Filter by sales reps if NOT all selected
      if (filters.assigned_to && filters.assigned_to.length > 0 && salesReps.length > 0 && filters.assigned_to.length < salesReps.length) {
        filteredLeads = filteredLeads.filter(lead => filters.assigned_to.includes(lead.assigned_to));
      }
      
      // Filter by sources if NOT all selected
      if (filters.sources && filters.sources.length > 0 && filters.sources.length < leadSourcesList.length) {
        filteredLeads = filteredLeads.filter(lead => filters.sources.includes(lead.lead_source));
      }
      
      // Filter by sub_sources if NOT all selected
      if (filters.sub_sources && filters.sub_sources.length > 0 && subSourcesList.length > 0 && filters.sub_sources.length < subSourcesList.length) {
        filteredLeads = filteredLeads.filter(lead => !lead.sub_source || filters.sub_sources.includes(lead.sub_source));
      }
      
      setAllFetchedLeads(filteredLeads);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    }
    setLoading(false);
  };

  const fetchSalesReps = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=sales_rep`, getAuthHeader());
      const reps = response.data || [];
      setSalesReps(reps);
      // Set all sales reps as selected by default when loaded
      setFilters(prev => ({
        ...prev,
        assigned_to: reps.map(rep => rep.name)
      }));
      setSalesRepsLoaded(true);
    } catch (error) {
      console.error('Error fetching sales reps:', error);
      setSalesRepsLoaded(true); // Still mark as loaded to show leads
    }
  };

  const fetchSubSources = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=sub_source`, getAuthHeader());
      const subs = (response.data || []).map(s => s.name);
      setSubSourcesList(subs);
      setFilters(prev => ({ ...prev, sub_sources: subs }));
    } catch (error) {
      console.error('Error fetching sub sources:', error);
    }
  };

  const fetchLeadSources = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=lead_source`, getAuthHeader());
      const sources = (response.data || []).map(s => s.name);
      setLeadSourcesList(sources);
      setFilters(prev => ({ ...prev, sources: sources }));
    } catch (error) {
      console.error('Error fetching lead sources:', error);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/leads/${leadId}`, getAuthHeader());
      toast.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    if (!window.confirm(`Delete ${selectedLeads.length} selected leads?`)) return;
    
    try {
      await Promise.all(selectedLeads.map(id => axios.delete(`${BACKEND_URL}/api/leads/${id}`, getAuthHeader())));
      toast.success(`${selectedLeads.length} leads deleted`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      console.error('Error deleting leads:', error);
      toast.error('Failed to delete some leads');
    }
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const toggleSelect = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Bulk Import Handler
  const handleBulkImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); e.target.value = ''; return; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_company_id', targetCo);
    
    try {
      toast.loading('Importing leads...');
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/leads/import/bulk`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.dismiss();
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`Some rows had errors: ${response.data.errors.slice(0, 5).join(', ')}`);
        console.log('Import errors:', response.data.errors);
      }
      fetchLeads();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Import failed');
    }
    e.target.value = '';
  };

  // Download Bulk Update Template
  const handleDownloadTemplate = async () => {
    try {
      toast.loading('Downloading template with your leads...');
      const response = await axios.get(`${BACKEND_URL}/api/leads/export/template`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      toast.dismiss();
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_bulk_update_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded! Edit the file and re-upload using Import button.');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to download template');
    }
  };

  // Calculate Lead Scores
  const handleCalculateScores = async () => {
    try {
      toast.loading('Calculating scores...');
      const response = await axios.get(`${BACKEND_URL}/api/leads/scoring/calculate`, getAuthHeader());
      toast.dismiss();
      toast.success(response.data.message);
      fetchLeads();
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to calculate scores');
    }
  };

  // Bulk Update Handler
  const handleBulkUpdate = async () => {
    if (selectedLeads.length === 0) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads/bulk-update`, {
        lead_ids: selectedLeads,
        updates: bulkUpdateData
      }, getAuthHeader());
      toast.success(response.data.message);
      setShowBulkUpdateModal(false);
      setBulkUpdateData({});
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    }
  };

  // ============ NEW LEADS IMPORT HANDLERS ============
  
  const fetchGoogleSheetsStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/google-sheets/status`, getAuthHeader());
      setGoogleSheetsStatus(response.data);
    } catch { /* ignore */ }
  };

  const handleDownloadNewTemplate = async () => {
    try {
      toast.loading('Downloading template...');
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/leads/import/new-template`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      toast.dismiss();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `new_leads_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch {
      toast.dismiss();
      toast.error('Failed to download template');
    }
  };

  const handleNewLeadImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsImporting(true);
    setImportResult(null);
    const targetCo = await ensureCompanyId();
    if (!targetCo) { toast.info('Upload cancelled — no target company selected'); setIsImporting(false); e.target.value = ''; return; }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_company_id', targetCo);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/leads/import/new`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setImportResult(response.data);
      if (response.data.created > 0) {
        toast.success(response.data.message);
        fetchLeads();
      }
    } catch (error) {
      setImportResult({ message: error.response?.data?.detail || 'Import failed', created: 0, errors: [] });
      toast.error(error.response?.data?.detail || 'Import failed');
    }
    setIsImporting(false);
    e.target.value = '';
  };

  const handleGoogleSheetsConnect = async () => {
    if (!googleSheetUrl.trim()) {
      toast.error('Please paste a Google Sheets URL');
      return;
    }
    setIsConnecting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads/google-sheets/connect`, {
        sheet_url: googleSheetUrl
      }, getAuthHeader());
      toast.success(response.data.message);
      setGoogleSheetUrl('');
      fetchGoogleSheetsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Google Sheet');
    }
    setIsConnecting(false);
  };

  const handleGoogleSheetsSyncNow = async () => {
    setIsSyncing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads/google-sheets/sync-now`, {}, getAuthHeader());
      if (response.data.status === 'success') {
        toast.success(`Synced! ${response.data.created} new leads created, ${response.data.duplicates} duplicates skipped`);
        fetchLeads();
      } else {
        toast.error(response.data.message || 'Sync failed');
      }
      fetchGoogleSheetsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    }
    setIsSyncing(false);
  };

  const handleGoogleSheetsDisconnect = async () => {
    if (!window.confirm('Disconnect Google Sheets sync? Previously synced leads will NOT be deleted.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/leads/google-sheets/disconnect`, {}, getAuthHeader());
      toast.success('Google Sheets disconnected');
      setGoogleSheetsStatus({ connected: false });
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  // WhatsApp Handler
  const openWhatsAppModal = (lead) => {
    setSelectedLead(lead);
    setShowWhatsAppModal(true);
  };

  // Email Handler
  const openEmailModal = (lead) => {
    setSelectedLead(lead);
    setShowEmailModal(true);
  };

  // Follow-up Handler
  const openFollowUpModal = (lead) => {
    setSelectedLead(lead);
    setShowFollowUpModal(true);
  };

  // Proforma Invoice Handler
  const openProformaModal = (lead) => {
    setSelectedLead(lead);
    setShowProformaModal(true);
  };

  const getStageStyle = (stage) => {
    const stageObj = STAGES.find(s => s.value === stage);
    return stageObj || { label: stage, color: 'bg-gray-500' };
  };

  const formatDate = (dateStr) => {
    return formatDateDDMMYYYY(dateStr);
  };

  return (
    <LeadsLayout user={user} onLogout={onLogout} title={viewMode === 'all' ? 'All Leads' : 'Follow-up Leads'}>
      {guardModal}
      <div className="space-y-4">
        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 bg-white rounded-xl p-1.5 shadow-sm border border-gray-100 w-fit" data-testid="leads-view-toggle">
          <button
            onClick={() => { setViewMode('all'); setFilters(prev => ({ ...prev, followup_date_from: '', followup_date_to: '' })); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'all'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="toggle-all-leads"
          >
            All Leads
          </button>
          <button
            onClick={() => { setViewMode('followup'); setFilters(prev => ({ ...prev, created_date_from: '', created_date_to: '' })); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'followup'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="toggle-followup-leads"
          >
            Follow-up Leads
          </button>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4 flex-wrap">
            <input
              type="text"
              placeholder="Search name, mobile, email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-64"
              data-testid="leads-search-input"
            />
            
            {/* Multi-select Stage Filter */}
            <div className="relative">
              <button
                onClick={() => { setShowStageFilter(!showStageFilter); setShowPriorityFilter(false); setShowSalesRepFilter(false); }}
                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 flex items-center gap-2 ${
                  filters.stages.length > 0 && filters.stages.length < STAGES.length 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200'
                }`}
                data-testid="leads-stage-filter"
              >
                <span>
                  {filters.stages.length === 0 || filters.stages.length === STAGES.length 
                    ? 'All Stages' 
                    : `${filters.stages.length} Stages`}
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showStageFilter && (
                <div className="absolute z-20 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                  <div className="p-2 border-b border-gray-100">
                    <div className="flex justify-between">
                      <button 
                        onClick={() => setFilters(prev => ({ ...prev, stages: STAGES.map(s => s.value) }))}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Select All
                      </button>
                      <button 
                        onClick={() => setFilters(prev => ({ ...prev, stages: getDefaultStages() }))}
                        className="text-xs text-emerald-600 hover:underline"
                      >
                        Default (No Won/Lost)
                      </button>
                      <button 
                        onClick={() => setFilters(prev => ({ ...prev, stages: [] }))}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  {STAGES.map(stage => (
                    <label key={stage.value} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.stages.includes(stage.value)}
                        onChange={() => toggleStageFilter(stage.value)}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <span className={`w-2 h-2 rounded-full ${stage.color}`}></span>
                      <span className="text-sm">{stage.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            {/* Multi-select Priority Filter */}
            <div className="relative">
              <button
                onClick={() => { setShowPriorityFilter(!showPriorityFilter); setShowStageFilter(false); setShowSalesRepFilter(false); setShowSourceFilter(false); }}
                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 flex items-center gap-2 ${
                  filters.priorities.length > 0 && filters.priorities.length < ALL_PRIORITIES.length 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200'
                }`}
                data-testid="leads-priority-filter"
              >
                <span>
                  {filters.priorities.length === 0 || filters.priorities.length === ALL_PRIORITIES.length 
                    ? 'All Priorities' 
                    : `${filters.priorities.length} Priorities`}
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showPriorityFilter && (
                <div className="absolute z-20 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200">
                  <div className="p-2 border-b border-gray-100 flex justify-between">
                    <button 
                      onClick={() => setFilters(prev => ({ ...prev, priorities: [...ALL_PRIORITIES] }))}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Select All
                    </button>
                    <button 
                      onClick={() => setFilters(prev => ({ ...prev, priorities: [] }))}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                  {[
                    { value: 'hot', label: '🔥 Hot', color: 'bg-red-100 text-red-700' },
                    { value: 'warm', label: '🌡️ Warm', color: 'bg-yellow-100 text-yellow-700' },
                    { value: 'cold', label: '❄️ Cold', color: 'bg-blue-100 text-blue-700' }
                  ].map(priority => (
                    <label key={priority.value} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes(priority.value)}
                        onChange={() => togglePriorityFilter(priority.value)}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${priority.color}`}>{priority.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            {/* Multi-select Sales Rep Filter */}
            <div className="relative">
              <button
                onClick={() => { setShowSalesRepFilter(!showSalesRepFilter); setShowStageFilter(false); setShowPriorityFilter(false); setShowSourceFilter(false); }}
                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 flex items-center gap-2 ${
                  filters.assigned_to.length > 0 && filters.assigned_to.length < salesReps.length 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200'
                }`}
                data-testid="leads-rep-filter"
              >
                <span>
                  {filters.assigned_to.length === 0 || filters.assigned_to.length === salesReps.length 
                    ? 'All Sales Reps' 
                    : `${filters.assigned_to.length} Reps`}
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showSalesRepFilter && (
                <div className="absolute z-20 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                  <div className="p-2 border-b border-gray-100 flex justify-between">
                    <button 
                      onClick={() => setFilters(prev => ({ ...prev, assigned_to: salesReps.map(r => r.name) }))}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Select All
                    </button>
                    <button 
                      onClick={() => setFilters(prev => ({ ...prev, assigned_to: [] }))}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                  {salesReps.map(rep => (
                    <label key={rep.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.assigned_to.includes(rep.name)}
                        onChange={() => toggleSalesRepFilter(rep.name)}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <span className="text-sm">{rep.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            {/* Multi-select Source Filter */}
            {leadSourcesList.length > 0 && (
            <div className="relative">
              <button
                onClick={() => { setShowSourceFilter(!showSourceFilter); setShowStageFilter(false); setShowPriorityFilter(false); setShowSalesRepFilter(false); }}
                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 flex items-center gap-2 ${
                  filters.sources.length > 0 && filters.sources.length < leadSourcesList.length 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200'
                }`}
                data-testid="leads-source-filter"
              >
                <span>
                  {filters.sources.length === 0 || filters.sources.length === leadSourcesList.length 
                    ? 'All Sources' 
                    : `${filters.sources.length} Sources`}
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showSourceFilter && (
                <div className="absolute z-20 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                  <div className="p-2 border-b border-gray-100 flex justify-between">
                    <button 
                      onClick={() => setFilters(prev => ({ ...prev, sources: [...leadSourcesList] }))}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Select All
                    </button>
                    <button 
                      onClick={() => setFilters(prev => ({ ...prev, sources: [] }))}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                  {leadSourcesList.map(source => (
                    <label key={source} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.sources.includes(source)}
                        onChange={() => toggleSourceFilter(source)}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <span className="text-sm">{source}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            )}
            
            {/* Multi-select Sub Source Filter */}
            {subSourcesList.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => { setShowSubSourceFilter(!showSubSourceFilter); setShowStageFilter(false); setShowPriorityFilter(false); setShowSalesRepFilter(false); setShowSourceFilter(false); }}
                  className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 flex items-center gap-2 ${
                    filters.sub_sources.length > 0 && filters.sub_sources.length < subSourcesList.length 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-gray-200'
                  }`}
                  data-testid="leads-sub-source-filter"
                >
                  <span>
                    {filters.sub_sources.length === 0 || filters.sub_sources.length === subSourcesList.length 
                      ? 'All Sub Sources' 
                      : `${filters.sub_sources.length} Sub Sources`}
                  </span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showSubSourceFilter && (
                  <div className="absolute z-20 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                    <div className="p-2 border-b border-gray-100 flex justify-between">
                      <button onClick={() => setFilters(prev => ({ ...prev, sub_sources: [...subSourcesList] }))} className="text-xs text-blue-600 hover:underline">Select All</button>
                      <button onClick={() => setFilters(prev => ({ ...prev, sub_sources: [] }))} className="text-xs text-red-600 hover:underline">Clear All</button>
                    </div>
                    {subSourcesList.map(sub => (
                      <label key={sub} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.sub_sources.includes(sub)}
                          onChange={() => setFilters(prev => {
                            const current = [...prev.sub_sources];
                            return { ...prev, sub_sources: current.includes(sub) ? current.filter(s => s !== sub) : [...current, sub] };
                          })}
                          className="w-4 h-4 text-emerald-600 rounded"
                        />
                        <span className="text-sm">{sub}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
              <span className="text-xs text-gray-500">Follow-up:</span>
              <input
                type="date"
                value={filters.followup_date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, followup_date_from: e.target.value }))}
                className="px-2 py-1 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="leads-date-from"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={filters.followup_date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, followup_date_to: e.target.value }))}
                className="px-2 py-1 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="leads-date-to"
              />
              {(filters.followup_date_from || filters.followup_date_to) && (
                <button
                  onClick={clearDateFilters}
                  className="text-red-500 hover:text-red-700 px-1"
                  title="Clear dates"
                >
                  ✕
                </button>
              )}
            </div>
            {/* Quick Follow-up Date Tabs (visible in follow-up view) */}
            {viewMode === 'followup' && (
              <div className="flex items-center gap-1">
                <button onClick={() => setFilters(prev => ({ ...prev, followup_date_from: getDateOffset(-1), followup_date_to: getDateOffset(-1) }))} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filters.followup_date_from === getDateOffset(-1) ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200'}`} data-testid="followup-prev-btn">Previous</button>
                <button onClick={() => setFilters(prev => ({ ...prev, followup_date_from: getToday(), followup_date_to: getToday() }))} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filters.followup_date_from === getToday() && filters.followup_date_to === getToday() ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200'}`} data-testid="followup-today-btn">Today</button>
                <button onClick={() => setFilters(prev => ({ ...prev, followup_date_from: getDateOffset(1), followup_date_to: getDateOffset(1) }))} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filters.followup_date_from === getDateOffset(1) ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'}`} data-testid="followup-tomorrow-btn">Tomorrow</button>
              </div>
            )}

            <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-lg border border-blue-200">
              <span className="text-xs text-blue-600">Created:</span>
              <input
                type="date"
                value={filters.created_date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, created_date_from: e.target.value }))}
                className="px-2 py-1 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="leads-created-date-from"
              />
              <span className="text-blue-400">to</span>
              <input
                type="date"
                value={filters.created_date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, created_date_to: e.target.value }))}
                className="px-2 py-1 border-0 bg-transparent focus:ring-0 text-sm"
                data-testid="leads-created-date-to"
              />
              {(filters.created_date_from || filters.created_date_to) && (
                <button
                  onClick={clearCreatedDateFilters}
                  className="text-red-500 hover:text-red-700 px-1"
                  title="Clear created dates"
                >
                  ✕
                </button>
              )}
            </div>
            {/* Quick Created Date Tabs (visible in All Leads view) */}
            {viewMode === 'all' && (
              <div className="flex items-center gap-1">
                <button onClick={() => setFilters(prev => ({ ...prev, created_date_from: getDateOffset(-1), created_date_to: getDateOffset(-1) }))} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filters.created_date_from === getDateOffset(-1) ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200'}`} data-testid="created-prev-btn">Previous</button>
                <button onClick={() => setFilters(prev => ({ ...prev, created_date_from: getToday(), created_date_to: getToday() }))} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filters.created_date_from === getToday() && filters.created_date_to === getToday() ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200'}`} data-testid="created-today-btn">Today</button>
              </div>
            )}
            
            {/* Sort Order Toggle */}
            <button
              onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-200 border border-gray-200 transition-all"
              title={sortOrder === 'desc' ? 'Latest first (click for oldest first)' : 'Oldest first (click for latest first)'}
              data-testid="sort-order-toggle"
            >
              <svg className={`w-3.5 h-3.5 transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
              </svg>
              {sortOrder === 'desc' ? 'Latest First' : 'Oldest First'}
            </button>
            
            {/* Reset Filters - show only if filters have been modified from defaults */}
            <button
              onClick={() => {
                setFilters({ 
                  stages: getDefaultStages(), 
                  priorities: [...ALL_PRIORITIES], 
                  assigned_to: salesReps.map(r => r.name), 
                  sources: [...leadSourcesList],
                  sub_sources: [...subSourcesList],
                  search: '', 
                  followup_date_from: '', 
                  followup_date_to: '', 
                  created_date_from: '', 
                  created_date_to: '' 
                });
                setShowStageFilter(false);
                setShowPriorityFilter(false);
                setShowSalesRepFilter(false);
                setShowSourceFilter(false);
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {leads.length} leads found
              {selectedLeads.length > 0 && ` • ${selectedLeads.length} selected`}
            </span>
            {selectedLeads.length > 0 && canDelete(RESOURCES.LEADS) && (
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200"
                data-testid="bulk-delete-btn"
              >
                🗑️ Delete Selected
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Export Dropdown */}
            <div className="relative group">
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium">
                📥 Export ▼
              </button>
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 hidden group-hover:block z-10">
                <button
                  onClick={async () => {
                    try {
                      const params = new URLSearchParams();
                      // Pass all active filters to export
                      if (filters.stages?.length && filters.stages.length < STAGES.length) {
                        filters.stages.forEach(s => params.append('stage', s));
                      }
                      if (filters.priorities?.length && filters.priorities.length < ALL_PRIORITIES.length) {
                        filters.priorities.forEach(p => params.append('priority', p));
                      }
                      if (filters.assigned_to?.length && filters.assigned_to.length < salesReps.length) {
                        filters.assigned_to.forEach(r => params.append('assigned_to', r));
                      }
                      if (filters.sources?.length && filters.sources.length < leadSourcesList.length) {
                        filters.sources.forEach(s => params.append('source', s));
                      }
                      if (filters.followup_date_from) params.append('followup_date_from', filters.followup_date_from);
                      if (filters.followup_date_to) params.append('followup_date_to', filters.followup_date_to);
                      if (filters.created_date_from) params.append('date_from', filters.created_date_from);
                      if (filters.created_date_to) params.append('date_to', filters.created_date_to);
                      if (filters.search) params.append('search', filters.search);
                      const resp = await axios.get(`${BACKEND_URL}/api/leads/export/excel?${params.toString()}`, { ...getAuthHeader(), responseType: 'blob' });
                      const url = window.URL.createObjectURL(resp.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Excel exported successfully');
                    } catch (e) { toast.error('Export failed'); }
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  📊 Export to Excel
                </button>
                <button
                  onClick={() => window.print()}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  📄 Print / PDF
                </button>
                <button
                  onClick={handleDownloadTemplate}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t"
                >
                  📋 Download Update Template
                </button>
              </div>
            </div>
            
            {/* Bulk Update */}
            {selectedLeads.length > 0 && (
              <button
                onClick={() => setShowBulkUpdateModal(true)}
                className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium"
                data-testid="bulk-update-btn"
              >
                Bulk Update ({selectedLeads.length})
              </button>
            )}
            
            {/* Import Dropdown - only show if user can create leads */}
            {canCreate(RESOURCES.LEADS) && (
              <div className="relative">
                <button
                  onClick={() => setShowImportDropdown(!showImportDropdown)}
                  className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm font-medium flex items-center gap-1"
                  data-testid="import-dropdown-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  Import
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showImportDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowImportDropdown(false)} />
                    <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20 py-1">
                      <button
                        onClick={() => { setShowNewImportModal(true); setShowImportDropdown(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-2"
                        data-testid="import-new-leads-btn"
                      >
                        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                        Import New Leads (Excel)
                      </button>
                      <label className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2 cursor-pointer border-t border-gray-100">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        Update Existing (Excel)
                        <input type="file" accept=".xlsx,.xls" onChange={(e) => { handleBulkImport(e); setShowImportDropdown(false); }} className="hidden" />
                      </label>
                      <button
                        onClick={() => { setShowGoogleSheetsModal(true); setShowImportDropdown(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-green-50 flex items-center gap-2 border-t border-gray-100"
                        data-testid="google-sheets-sync-btn"
                      >
                        <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M19.385 2H8.615C7.724 2 7 2.724 7 3.615v2.77H4.615C3.724 6.385 3 7.109 3 8v12.385C3 21.276 3.724 22 4.615 22h10.77c.891 0 1.615-.724 1.615-1.615v-2.77h2.385c.891 0 1.615-.724 1.615-1.615V3.615C21 2.724 20.276 2 19.385 2zM4.615 20.385V8h10.77v12.385H4.615zm14.77-4h-2.77V8c0-.891-.724-1.615-1.615-1.615H8.615V3.615h10.77v12.77z"/></svg>
                        Google Sheets Sync
                        {googleSheetsStatus.connected && <span className="ml-auto w-2 h-2 bg-green-500 rounded-full" />}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Google Sheets connected indicator */}
            {googleSheetsStatus.connected && (
              <button
                onClick={() => setShowGoogleSheetsModal(true)}
                className="px-2 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-xs font-medium flex items-center gap-1 border border-green-200"
                data-testid="sheets-status-btn"
                title="Google Sheets is connected"
              >
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Sheets Synced
              </button>
            )}
            
            {/* Calculate Scores */}
            <button
              onClick={handleCalculateScores}
              className="px-3 py-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 text-sm font-medium"
              title="Calculate lead scores"
            >
              🎯 Score All
            </button>
            
            {/* Add New Lead - only show if user can create leads */}
            {canCreate(RESOURCES.LEADS) && (
              <button
                onClick={() => navigate('/leads/add')}
                  className="px-4 py-2 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
                data-testid="add-lead-btn"
              >
                + Add New Lead
              </button>
            )}
          </div>
        </div>

        {/* Leads Count and Pagination Info */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <p className="text-sm text-gray-600">
            {leads.length} leads found
            {leads.length > 0 && ` • Showing ${Math.min((currentPage - 1) * rowsPerPage + 1, leads.length)}-${Math.min(currentPage * rowsPerPage, leads.length)} of ${leads.length}`}
          </p>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Rows per page:</label>
            <select
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* Leads Cards - 2-Row Layout */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-64 bg-white rounded-xl shadow-sm">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="empty-state" style={{ borderColor: 'rgba(16,185,129,0.20)' }}>
              <div className="empty-state-icon" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.14) 0%, rgba(20,184,166,0.10) 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-emerald-600">
                  <circle cx="11" cy="11" r="6" />
                  <path d="M11 7v4l3 2M21 21l-4.3-4.3" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-800 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>No leads yet</h3>
              <p className="text-sm text-slate-500 mt-1.5 max-w-md">Capture your first lead to start building the pipeline. Once added, you can track follow-ups, convert to bookings, and measure rep performance.</p>
              {canCreate(RESOURCES.LEADS) && (
                <button
                  onClick={() => navigate('/leads/add')}
                  className="mt-5 px-5 py-2.5 text-white rounded-xl text-sm font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
                  data-testid="empty-add-lead"
                >
                  + Add Your First Lead
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Select All Row */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-600">
                    {selectedLeads.length > 0 ? `${selectedLeads.length} selected` : 'Select all'}
                  </span>
                </div>
                {selectedLeads.length > 0 && canUpdate(RESOURCES.LEADS) && (
                  <button
                    onClick={() => setShowBulkUpdateModal(true)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Bulk Update
                  </button>
                )}
              </div>

              {/* Lead Cards */}
              {leads
                .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                .map((lead, idx) => {
                  const stageStyle = getStageStyle(lead.stage);
                  const avatarColor = getAvatarColor(lead.first_name);
                  const isWon = lead.stage === 'won';
                  const serialNo = (currentPage - 1) * rowsPerPage + idx + 1;
                  
                  return (
                    <div 
                      key={lead.id}
                      className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${isWon ? 'ring-2 ring-green-400' : ''}`}
                      data-testid={`lead-card-${lead.id}`}
                    >
                      {/* Row 1: Lead Info + Checkbox */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(lead.id)}
                            onChange={() => toggleSelect(lead.id)}
                            className="w-4 h-4 mt-1 text-emerald-600 rounded focus:ring-emerald-500"
                          />
                          <span className="text-xs font-semibold text-gray-400 mt-1 w-6 text-center flex-shrink-0" data-testid="lead-serial-no">{serialNo}</span>
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${avatarColor} flex-shrink-0`}>
                            {lead.first_name?.[0]?.toUpperCase() || '?'}
                            {lead.last_name?.[0]?.toUpperCase() || ''}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-800 truncate">
                                {lead.first_name} {lead.last_name || ''}
                                {isWon && <span className="ml-1">🏆</span>}
                              </h3>
                              <span className="text-xs text-gray-500">#{lead.lead_no}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <a href={`tel:${lead.mobile}`} className="text-sm text-blue-600 hover:underline">
                                📞 {lead.mobile}
                              </a>
                              {lead.email && (
                                <span className="text-xs text-gray-500 truncate max-w-[150px]">
                                  ✉️ {lead.email}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${stageStyle.color}`}>
                                {stageStyle.label}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                lead.priority === 'hot' ? 'bg-red-100 text-red-700' :
                                lead.priority === 'warm' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {lead.priority === 'hot' ? '🔥 Hot' : lead.priority === 'warm' ? '🌡️ Warm' : '❄️ Cold'}
                              </span>
                              {lead.lead_grade && (
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  lead.lead_grade === 'A' ? 'bg-green-100 text-green-700' :
                                  lead.lead_grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                  lead.lead_grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  Grade {lead.lead_grade}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Details + Actions */}
                      <div className="p-3 bg-gray-50">
                        {/* Recent Follow-up Activity Row */}
                        {lead.follow_up_history && lead.follow_up_history.length > 0 && (
                          <div className="mb-2 pb-2 border-b border-gray-200">
                            <div className="text-xs space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-500 font-medium">Last Activity:</span>
                                {/* Call Status */}
                                {lead.follow_up_history[0]?.call_status && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-medium">
                                    📞 {lead.follow_up_history[0].call_status}
                                  </span>
                                )}
                                {/* Follow-up Status */}
                                {lead.follow_up_history[0]?.followup_status && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-medium">
                                    {lead.follow_up_history[0].followup_status}
                                  </span>
                                )}
                                {/* Sub Lead Status */}
                                {lead.follow_up_history[0]?.sub_lead_status && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-medium">
                                    {lead.follow_up_history[0].sub_lead_status}
                                  </span>
                                )}
                                {/* Date & Time */}
                                <span className="text-gray-400 ml-auto text-[10px]">
                                  {lead.follow_up_history[0]?.created_at ? new Date(lead.follow_up_history[0].created_at).toLocaleDateString('en-GB') : ''}
                                  {lead.follow_up_history[0]?.created_at && (
                                    <span className="ml-1">
                                      {new Date(lead.follow_up_history[0].created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </span>
                              </div>
                              {/* Remarks */}
                              {lead.follow_up_history[0]?.remarks && (
                                <div className="text-gray-600 bg-gray-100 rounded px-2 py-1 mt-1">
                                  <span className="text-gray-400 mr-1">📝</span>
                                  {lead.follow_up_history[0].remarks}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Fallback to activities if no follow_up_history */}
                        {(!lead.follow_up_history || lead.follow_up_history.length === 0) && lead.activities && lead.activities.length > 0 && (
                          <div className="mb-2 pb-2 border-b border-gray-200">
                            <div className="text-xs">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-500 font-medium">Last Activity:</span>
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[10px] font-medium">
                                  {lead.activities[0]?.activity_type?.replace(/_/g, ' ') || 'Activity'}
                                </span>
                                {lead.activities[0]?.description && (
                                  <span className="text-gray-600 truncate max-w-[300px]">
                                    {lead.activities[0].description}
                                  </span>
                                )}
                                <span className="text-gray-400 ml-auto text-[10px]">
                                  {lead.activities[0]?.timestamp ? new Date(lead.activities[0].timestamp).toLocaleDateString('en-GB') : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2">
                          {/* Left: Details */}
                          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500 block">Interest</span>
                              <span className="text-gray-800 font-medium truncate block">
                                {lead.interested_model || '-'}
                                {lead.interested_variant && ` / ${lead.interested_variant}`}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 block">Source</span>
                              <span className="text-gray-800 font-medium truncate block">{lead.lead_source || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block">Sales Rep</span>
                              <span className="text-gray-800 font-medium truncate block">{lead.assigned_to || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block">Follow-up</span>
                              {lead.next_follow_up_date ? (
                                <span className={`font-medium ${
                                  new Date(lead.next_follow_up_date) < new Date()
                                    ? 'text-red-600'
                                    : 'text-blue-600'
                                }`}>
                                  {formatDate(lead.next_follow_up_date)}
                                  {lead.next_follow_up_time && (
                                    <span className="text-xs ml-1">{lead.next_follow_up_time}</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-gray-400">Not set</span>
                              )}
                            </div>
                          </div>

                          {/* Right: Action Icons */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => openFollowUpModal(lead)}
                              className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                              title="View Follow-up History"
                            >
                              <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {/* Generate Proforma Invoice */}
                            <button
                              onClick={() => openProformaModal(lead)}
                              className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg transition-colors"
                              title="Generate Proforma Invoice"
                              data-testid={`proforma-btn-${lead.id}`}
                            >
                              <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                            {/* Send Catalog */}
                            <button
                              onClick={() => { setSelectedLead(lead); setShowCatalogModal(true); }}
                              className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                              title="Send Catalog"
                              data-testid={`catalog-btn-${lead.id}`}
                            >
                              <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </button>
                            {canUpdate(RESOURCES.LEADS) && (
                              <button
                                onClick={() => navigate(`/leads/edit/${lead.id}`)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                            <a
                              href={`tel:${lead.mobile}`}
                              className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                              title="Call"
                            >
                              <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </a>
                            {lead.mobile && (
                              <button
                                onClick={() => openWhatsAppModal(lead)}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                title="WhatsApp"
                              >
                                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current pointer-events-none">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                              </button>
                            )}
                            {lead.email && (
                              <button
                                onClick={() => openEmailModal(lead)}
                                className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                                title="Email"
                              >
                                <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </button>
                            )}
                            {canDelete(RESOURCES.LEADS) && (
                              <button
                                onClick={() => handleDelete(lead.id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              {/* Pagination Controls */}
              {leads.length > rowsPerPage && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm text-gray-600">
                      Page {currentPage} of {Math.ceil(leads.length / rowsPerPage)}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ««
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div className="hidden sm:flex items-center gap-1">
                        {Array.from({ length: Math.min(5, Math.ceil(leads.length / rowsPerPage)) }, (_, i) => {
                          const totalPages = Math.ceil(leads.length / rowsPerPage);
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 text-sm rounded ${
                                currentPage === pageNum
                                  ? 'bg-emerald-600 text-white'
                                  : 'border border-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(p => Math.min(Math.ceil(leads.length / rowsPerPage), p + 1))}
                        disabled={currentPage === Math.ceil(leads.length / rowsPerPage)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.ceil(leads.length / rowsPerPage))}
                        disabled={currentPage === Math.ceil(leads.length / rowsPerPage)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        »»
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Bulk Update Modal */}
        {showBulkUpdateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Bulk Update {selectedLeads.length} Leads</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                  <select
                    value={bulkUpdateData.stage || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, stage: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- No Change --</option>
                    {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={bulkUpdateData.priority || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- No Change --</option>
                    <option value="hot">Hot</option>
                    <option value="warm">Warm</option>
                    <option value="cold">Cold</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <select
                    value={bulkUpdateData.assigned_to || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, assigned_to: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- No Change --</option>
                    {salesReps.map(rep => <option key={rep.id} value={rep.name}>{rep.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
                  <select
                    value={bulkUpdateData.lead_source || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, lead_source: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- No Change --</option>
                    {LEAD_SOURCES.map(src => <option key={src} value={src}>{src}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
                  <input
                    type="date"
                    value={bulkUpdateData.next_follow_up_date || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, next_follow_up_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Time</label>
                  <input
                    type="time"
                    value={bulkUpdateData.next_follow_up_time || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, next_follow_up_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="bulk-update-followup-time"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Finance Status</label>
                  <select
                    value={bulkUpdateData.finance_status || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, finance_status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- No Change --</option>
                    <option value="login">Login</option>
                    <option value="fi_pending">FI Pending</option>
                    <option value="bank_statement_pending">Bank Statement Pending</option>
                    <option value="clarification_pending">Clarification Pending</option>
                    <option value="enach_pending">E-Nach Pending</option>
                    <option value="ipr_invoice_pending">IPR/Invoice Pending</option>
                    <option value="disbursement_pending">Disbursement Pending</option>
                    <option value="disbursement_done">Disbursement Done</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea
                    value={bulkUpdateData.remarks || ''}
                    onChange={(e) => setBulkUpdateData(prev => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={2}
                    placeholder="Add bulk remarks..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => { setShowBulkUpdateModal(false); setBulkUpdateData({}); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpdate}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Update {selectedLeads.length} Leads
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Modal */}
        {showEmailModal && selectedLead && (
          <EmailModal
            lead={selectedLead}
            onClose={() => { setShowEmailModal(false); setSelectedLead(null); }}
          />
        )}

        {/* WhatsApp Modal */}
        {showWhatsAppModal && selectedLead && (
          <WhatsAppModal
            lead={selectedLead}
            onClose={() => { setShowWhatsAppModal(false); setSelectedLead(null); }}
          />
        )}

        {/* Follow-up Modal */}
        {showFollowUpModal && selectedLead && (
          <FollowUpModal
            lead={selectedLead}
            onClose={() => { setShowFollowUpModal(false); setSelectedLead(null); }}
            onUpdate={fetchLeads}
          />
        )}

        {/* Proforma Invoice Modal */}
        {showProformaModal && selectedLead && (
          <ProformaModal
            lead={selectedLead}
            onClose={() => { setShowProformaModal(false); setSelectedLead(null); }}
          />
        )}

        {/* Catalog Modal */}
        {showCatalogModal && selectedLead && (
          <CatalogModal
            lead={selectedLead}
            onClose={() => { setShowCatalogModal(false); setSelectedLead(null); }}
          />
        )}

        {/* ============ NEW LEADS IMPORT MODAL ============ */}
        {showNewImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowNewImportModal(false); setImportResult(null); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Import New Leads</h2>
                    <p className="text-white/80 text-xs">Upload Excel file to create leads</p>
                  </div>
                </div>
                <button onClick={() => { setShowNewImportModal(false); setImportResult(null); }} className="text-white/80 hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Step 1: Download Template */}
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Download the template</p>
                    <p className="text-xs text-gray-500 mt-1">Get the Excel template with the correct columns and fill in your leads data.</p>
                    <button
                      onClick={handleDownloadNewTemplate}
                      className="mt-2 px-4 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                      data-testid="download-new-template-btn"
                    >
                      <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Download Template
                    </button>
                  </div>
                </div>

                {/* Step 2: Upload */}
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">Upload filled Excel file</p>
                    <p className="text-xs text-gray-500 mt-1">Required fields: First Name, Mobile. Duplicates (same mobile) are auto-skipped.</p>
                    <label className={`mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${isImporting ? 'bg-gray-300 text-gray-500' : 'bg-orange-500 text-white hover:bg-orange-600'}`}>
                      {isImporting ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Importing...</>
                      ) : (
                        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg> Choose File</>
                      )}
                      <input type="file" accept=".xlsx,.xls" onChange={handleNewLeadImport} className="hidden" disabled={isImporting} data-testid="new-leads-file-input" />
                    </label>
                  </div>
                </div>

                {/* Import Results */}
                {importResult && (
                  <div className={`p-4 rounded-xl border ${importResult.created > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="import-result">
                    <div className="flex items-center gap-2 mb-2">
                      {importResult.created > 0 ? (
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      )}
                      <p className={`text-sm font-semibold ${importResult.created > 0 ? 'text-green-800' : 'text-red-800'}`}>{importResult.message}</p>
                    </div>
                    <div className="flex gap-4 text-xs">
                      {importResult.created !== undefined && <span className="text-green-700">Created: {importResult.created}</span>}
                      {importResult.duplicates > 0 && <span className="text-yellow-700">Duplicates: {importResult.duplicates}</span>}
                      {importResult.total_processed > 0 && <span className="text-gray-600">Total rows: {importResult.total_processed}</span>}
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2 max-h-24 overflow-y-auto">
                        {importResult.errors.map((err, i) => <p key={i} className="text-xs text-red-600">{err}</p>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============ GOOGLE SHEETS SYNC MODAL ============ */}
        {showGoogleSheetsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowGoogleSheetsModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 50%, #0891B2 100%)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M19.385 2H8.615C7.724 2 7 2.724 7 3.615v2.77H4.615C3.724 6.385 3 7.109 3 8v12.385C3 21.276 3.724 22 4.615 22h10.77c.891 0 1.615-.724 1.615-1.615v-2.77h2.385c.891 0 1.615-.724 1.615-1.615V3.615C21 2.724 20.276 2 19.385 2zM4.615 20.385V8h10.77v12.385H4.615zm14.77-4h-2.77V8c0-.891-.724-1.615-1.615-1.615H8.615V3.615h10.77v12.77z"/></svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Google Sheets Sync</h2>
                    <p className="text-white/80 text-xs">Auto-import leads from a shared sheet</p>
                  </div>
                </div>
                <button onClick={() => setShowGoogleSheetsModal(false)} className="text-white/80 hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                {!googleSheetsStatus.connected ? (
                  <>
                    {/* Setup instructions */}
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <h3 className="text-sm font-semibold text-blue-800 mb-2">How it works</h3>
                      <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                        <li>Open your Google Sheet with leads data</li>
                        <li>Click <strong>Share</strong> and set to <strong>"Anyone with the link can view"</strong></li>
                        <li>Copy the share link and paste it below</li>
                        <li>Required columns: <strong>First Name</strong>, <strong>Mobile</strong></li>
                      </ol>
                    </div>

                    {/* URL Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Google Sheets URL</label>
                      <input
                        type="url"
                        value={googleSheetUrl}
                        onChange={(e) => setGoogleSheetUrl(e.target.value)}
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        data-testid="google-sheet-url-input"
                      />
                    </div>

                    <button
                      onClick={handleGoogleSheetsConnect}
                      disabled={isConnecting || !googleSheetUrl.trim()}
                      className="w-full py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                      data-testid="connect-sheets-btn"
                    >
                      {isConnecting ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Connecting...</>
                      ) : (
                        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg> Connect Sheet</>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Connected status */}
                    <div className="p-4 bg-green-50 rounded-xl border border-green-200" data-testid="sheets-connected-status">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                        <h3 className="text-sm font-semibold text-green-800">Sheet Connected</h3>
                      </div>
                      <div className="text-xs text-green-700 space-y-1">
                        <p className="truncate">URL: <a href={googleSheetsStatus.sheet_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">{googleSheetsStatus.sheet_url}</a></p>
                        {googleSheetsStatus.last_sync_at && (
                          <p>Last sync: {formatDateDDMMYYYY(googleSheetsStatus.last_sync_at)} at {new Date(googleSheetsStatus.last_sync_at).toLocaleTimeString()}</p>
                        )}
                        <p>Total synced leads: {googleSheetsStatus.total_synced || 0}</p>
                        {googleSheetsStatus.last_sync_result && (
                          <p>Last result: {googleSheetsStatus.last_sync_result.status === 'success' 
                            ? `${googleSheetsStatus.last_sync_result.created} created, ${googleSheetsStatus.last_sync_result.duplicates} duplicates` 
                            : googleSheetsStatus.last_sync_result.message || 'Unknown'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Sync Now button */}
                    <button
                      onClick={handleGoogleSheetsSyncNow}
                      disabled={isSyncing}
                      className="w-full py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                      data-testid="sync-now-btn"
                    >
                      {isSyncing ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Syncing...</>
                      ) : (
                        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Sync Now</>
                      )}
                    </button>

                    {/* Disconnect */}
                    <button
                      onClick={handleGoogleSheetsDisconnect}
                      className="w-full py-2 text-red-600 border border-red-200 rounded-xl text-sm hover:bg-red-50 transition-colors"
                      data-testid="disconnect-sheets-btn"
                    >
                      Disconnect Google Sheet
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </LeadsLayout>
  );
}


export default LeadsList;
